import { CustomCheckResult } from '@internal/plugin-tech-insights-backend-module-maturity';
import {
  MaturityScore,
  MaturityScoreArea,
  MaturitySummary,
  MaturityRank,
  Rank,
  MaturitySummaryArea,
} from '../types';

/**
 * Formats scoring data received from TechInsightsPlugin checks run
 */
export class ScoringDataFormatter {
  getMaturityRank(results: CustomCheckResult[]): MaturityRank {
    return this.calculateRank(results);
  }

  generateMaturityScore(results: CustomCheckResult[]): MaturityScore {
    const { rank, isMaxRank } = this.calculateRank(results);
    return {
      rank,
      isMaxRank,
      areaScores: this.generateMaturityScoreArea(results),
    };
  }

  generateMaturitySummary(results: CustomCheckResult[]): MaturitySummary {
    const { rank, isMaxRank } = this.calculateRank(results);
    const maxRank = this.calculateMaxRank(results);
    const nextRank = isMaxRank ? maxRank : rank + 1;
    const scoresByArea = this.groupScoresByAreaName(results);
    let totalChecks = 0;
    let passedChecks = 0;
    let rankTotalChecks = 0;
    let rankPassedChecks = 0;

    const areaSummaries: MaturitySummaryArea[] = Object.entries(scoresByArea)
      .map(([areaName, areaResults]) => {
        const { rank: areaRank, isMaxRank: areaIsMaxRank } =
          this.calculateRank(areaResults);
        const areaMaxRank = this.calculateMaxRank(areaResults);
        const areaNextRank = areaIsMaxRank ? areaMaxRank : areaRank + 1;
        let areaTotalChecks = 0;
        let areaPassedChecks = 0;

        // Count checks for each rank because overall next rank may be different from area next rank
        const areaRankTotalChecks: { [key: number]: number } = {
          [Rank.Stone]: 0,
          [Rank.Bronze]: 0,
          [Rank.Silver]: 0,
          [Rank.Gold]: 0,
          [Rank.Diamond]: 0,
        };
        const areaRankPassedChecks: { [key: number]: number } = {
          [Rank.Stone]: 0,
          [Rank.Bronze]: 0,
          [Rank.Silver]: 0,
          [Rank.Gold]: 0,
          [Rank.Diamond]: 0,
        };

        areaResults.forEach(checkResult => {
          areaTotalChecks++;
          if (checkResult.result) {
            areaPassedChecks++;
          }

          // Count check progress in each rank
          areaRankTotalChecks[checkResult.check.rank]++;
          if (checkResult.result) {
            areaRankPassedChecks[checkResult.check.rank]++;
          }
        });

        // Aggregate overall check progress
        totalChecks += areaTotalChecks;
        passedChecks += areaPassedChecks;
        // Aggregate check progress from checks in next overall rank
        rankTotalChecks += areaRankTotalChecks[nextRank];
        rankPassedChecks += areaRankPassedChecks[nextRank];

        const areaProgPercent = this.calculatePercent(
          areaPassedChecks,
          areaTotalChecks,
        );
        const areaRankProgPercent = this.calculatePercent(
          areaRankPassedChecks[areaNextRank],
          areaRankTotalChecks[areaNextRank],
        );

        return {
          area: areaName,
          progress: {
            passedChecks: areaPassedChecks,
            totalChecks: areaTotalChecks,
            percentage: areaProgPercent,
          },
          rankProgress: {
            passedChecks: areaRankPassedChecks[areaNextRank],
            totalChecks: areaRankTotalChecks[areaNextRank],
            percentage: areaRankProgPercent,
          },
          rank: areaRank,
          maxRank: areaMaxRank,
          isMaxRank: areaIsMaxRank,
        };
      })
      .sort((a, b) => (a.area > b.area ? -1 : 1)); // Sort according to area name

    const progPercent = this.calculatePercent(passedChecks, totalChecks);
    const rankProgPercent = this.calculatePercent(
      rankPassedChecks,
      rankTotalChecks,
    );

    return {
      points: this.calculatePoints(results),
      progress: {
        passedChecks,
        totalChecks,
        percentage: progPercent,
      },
      rankProgress: {
        passedChecks: rankPassedChecks,
        totalChecks: rankTotalChecks,
        percentage: rankProgPercent,
      },
      rank,
      maxRank,
      isMaxRank,
      areaSummaries,
    };
  }

  private generateMaturityScoreArea(
    results: CustomCheckResult[],
  ): MaturityScoreArea[] {
    const scoresByArea = this.groupScoresByAreaName(results);

    return Object.entries(scoresByArea).map(([area, checkResults]) => {
      const { rank, isMaxRank } = this.calculateRank(checkResults);
      return {
        area,
        rank,
        isMaxRank,
        checkResults,
      };
    });
  }

  /**
   * Some checks within one Area are divided as they should be applied for certain types of Components only
   * When displaying Area scores in the UI they should be concatenated again for total Area score calculation
   */
  private groupScoresByAreaName(results: CustomCheckResult[]) {
    const scoresByAreaName: {
      [key: string]: CustomCheckResult[];
    } = {};

    results.forEach(checkResult => {
      scoresByAreaName[checkResult.check.type] ??= [];
      scoresByAreaName[checkResult.check.type].push(checkResult);
    });

    return scoresByAreaName;
  }

  /** Calculated rank based on the total performance of all areas */
  private calculateRank(results: CustomCheckResult[]): MaturityRank {
    const maxRank = this.calculateMaxRank(results);
    let rank = maxRank; // Assume maximum rank

    // Reduce rank if a failing check for a lower rank exists
    results.forEach(checkResult => {
      if (!checkResult.result && checkResult.check.rank <= rank) {
        rank = checkResult.check.rank - 1;
      }
    });

    const isMaxRank = rank === maxRank;

    return { rank, isMaxRank };
  }

  private calculateMaxRank(results: CustomCheckResult[]): Rank {
    let rank: Rank = Rank.Stone;

    // Get the highest rank achievable for this area
    results.forEach(checkResult => {
      if (checkResult.check.rank > rank) {
        rank = checkResult.check.rank;
      }
    });

    return rank;
  }

  private calculatePoints(results: CustomCheckResult[]): number {
    let points = 0;
    results
      .filter(checkResult => checkResult.result)
      .forEach(checkResult => {
        points += checkResult.check.exp;
      });

    return points;
  }

  calculatePercent(passedChecks: number, totalChecks: number): number {
    // Prevent dividing by zero
    return totalChecks === 0
      ? 0
      : Math.round((passedChecks / totalChecks) * 100);
  }
}
